<template>
  <Modal :model-value="isOpen" title="Change Password" @update:model-value="$emit('close')">
    <div class="space-y-4">
      <p v-if="error" class="text-sm text-red-500">{{ error }}</p>

      <div>
        <label class="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-300">Current Password</label>
        <div class="relative">
          <input
            :type="showCurrent ? 'text' : 'password'"
            v-model="localForm.currentPassword"
            class="w-full rounded-xl px-4 py-3 pr-10 text-sm bg-white/70 dark:bg-slate-900/40
                   border border-slate-200/60 dark:border-slate-700/60
                   text-slate-800 dark:text-slate-100
                   placeholder:text-slate-400 dark:placeholder:text-slate-500
                   shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2
                   focus:ring-offset-white dark:focus:ring-offset-slate-900
                   focus:ring-[#ff8e48]/60"
          />
          <button type="button" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            @click="showCurrent = !showCurrent">
            <EyeOff v-if="!showCurrent" class="h-4 w-4" />
            <Eye v-else class="h-4 w-4" />
          </button>
        </div>
      </div>

      <div>
        <label class="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-300">New Password</label>
        <div class="relative">
          <input
            :type="showNew ? 'text' : 'password'"
            v-model="localForm.newPassword"
            class="w-full rounded-xl px-4 py-3 pr-10 text-sm bg-white/70 dark:bg-slate-900/40
                   border border-slate-200/60 dark:border-slate-700/60
                   text-slate-800 dark:text-slate-100
                   placeholder:text-slate-400 dark:placeholder:text-slate-500
                   shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2
                   focus:ring-offset-white dark:focus:ring-offset-slate-900
                   focus:ring-[#ff8e48]/60"
          />
          <button type="button" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            @click="showNew = !showNew">
            <EyeOff v-if="!showNew" class="h-4 w-4" />
            <Eye v-else class="h-4 w-4" />
          </button>
        </div>
      </div>

      <div>
        <label class="mb-1 block text-xs font-semibold text-slate-700 dark:text-slate-300">Confirm New Password</label>
        <div class="relative">
          <input
            :type="showConfirm ? 'text' : 'password'"
            v-model="localForm.confirmPassword"
            class="w-full rounded-xl px-4 py-3 pr-10 text-sm bg-white/70 dark:bg-slate-900/40
                   border border-slate-200/60 dark:border-slate-700/60
                   text-slate-800 dark:text-slate-100
                   placeholder:text-slate-400 dark:placeholder:text-slate-500
                   shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2
                   focus:ring-offset-white dark:focus:ring-offset-slate-900
                   focus:ring-[#ff8e48]/60"
          />
          <button type="button" class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            @click="showConfirm = !showConfirm">
            <EyeOff v-if="!showConfirm" class="h-4 w-4" />
            <Eye v-else class="h-4 w-4" />
          </button>
        </div>
        <p v-if="localForm.newPassword && localForm.confirmPassword && localForm.newPassword !== localForm.confirmPassword"
          class="mt-1 text-xs text-red-500">Passwords do not match</p>
      </div>
    </div>

    <template #footer>
      <button
        type="button"
        class="inline-flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
        @click="$emit('close')"
      >
        Cancel
      </button>
      <button
        type="button"
        class="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-primary to-secondary px-4 py-2 text-sm font-semibold text-white
               shadow-sm hover:brightness-110 transition disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="loading"
        @click="handleSave"
      >
        Update Password
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue'
import { Eye, EyeOff } from 'lucide-vue-next'
import Modal from '@/components/layout/Modal.vue'

interface PasswordForm {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

const props = defineProps<{
  isOpen: boolean
  passwordForm: PasswordForm
  loading: boolean
  error?: string
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', form: PasswordForm): void
}>()

const localForm = reactive<PasswordForm>({ ...props.passwordForm })
const showCurrent = ref(false)
const showNew = ref(false)
const showConfirm = ref(false)

watch(() => props.passwordForm, (val) => {
  Object.assign(localForm, val)
}, { deep: true })

import { ref } from 'vue'

const handleSave = () => {
  emit('save', { ...localForm })
}
</script>
